import _, {chain, compact, concat, findLast, first, isUndefined, last, partition} from 'lodash';
import React, {Component, FC, Key, ReactNode, useMemo, useState} from 'react';
import styled from 'styled-components';

import {getTicketStatusTags} from '../../../../../../libs/inboxes/store/src/ticketing/ticketStatusSelectors';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useDebounce} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncMoreStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  FocusModesEnum,
  LoadMoreHandler,
  ScrollDirectionsEnum,
} from '../../../../../../libs/shared/core/src/helpers/types/scrollerTypes';
import {onlyItem} from '../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {ChannelModel} from '../../../../../../libs/shared/core/src/models/api/channelModel';
import {InboundOptimisticMessage} from '../../../../../../libs/shared/core/src/models/api/chatDirectPushMessageModel';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {CommonTagModel} from '../../../../../../libs/shared/core/src/models/api/commonTagModel';
import {ConversationTeamStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {
  DraftMessageModel,
  MessageModel,
  TimelineMessageModel,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {usePush} from '../../../../../../libs/shared/core/src/services/push/pushContext';
import {ResourceMap} from '../../../../../../libs/shared/core/src/store/commonAppTypes';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {Loader, LoaderVariantsEnum} from '../../../../../../libs/shared/ui/src/components/loaders/loader';
import {DynamicScrollBoundaries} from '../../../../../../libs/shared/ui/src/components/scroll/dynamicScrollBoundaries';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {
  filterStaticViewerBannerActivity,
  StaticViewerActivity,
} from '../../../../../../srcCommon/inboxes/viewer/activity/static/staticViewerHelpers';
import {
  findFocusKey,
  findLastActivityId,
  findLastTimelineManualMessage,
  isActivityIdFocusKey,
  LastTimelineMessage,
  MILESTONE_ACTIVITY,
} from '../../../../../../srcCommon/inboxes/viewer/conversation/timeline/viewerTimelineHelpers';
import {startSimplePerformanceInteraction} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {commentOnlyHasAnnotation} from '../../../../../helpers/models/commentHelpers';
import {
  isActivityInConversationRelevant,
  isConversationInTeamContext,
  makeConversationActionContext,
} from '../../../../../helpers/models/conversationHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivitySplitOriginModel,
  BasicActivitySplitTargetModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
  isMessageActivity,
  MessageActivityTypesEnum,
} from '../../../../../models/api/activityModel';
import {
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../../../models/api/activitySourceModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {isBasicActivityCollapsible} from '../../../../../store/data/activities/activitiesCollapseSelectors';
import {FindTeammate} from '../../../../../store/data/teammates/teammatesSelectors';
import {findParametersActivityId} from '../../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {findOriginalLastActivityDateForViewerPage} from '../../../../../store/panels/viewer/viewerPanelHelpers';
import {SendingActivity} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelConversationPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {helpCenterUrl} from '../../../../../store/stateless/navigationActions';
import {VariableHeightScroller} from '../../../../common/scroll/variableHeightScroller';
import {VisibleItemsChangeHandler} from '../../../../common/scroll/variableHeightScrollerBase';
import {ActivityRefs, InfiniteScrollerProps} from '../../../../common/scroll/variableHeightScrollerTypes';
import {ChatPushProps} from '../../../../push/chatPushContext';
import {timelineScrollerClassName} from '../../../composer/composerCommonClassnames';
import {DisabledChatComposerPlaceholder} from '../../../composer/disabledComposerPlaceholder';
import {ActivityViewer} from '../../activity/activityViewer';
import {BasicViewDataRetentionBannerContainer} from '../../activity/basic/basicViewDataRetentionBannerContainer';
import {BasicViewerGroup} from '../../activity/basic/basicViewerGroup';
import {ChatDirectPushMessage} from '../../activity/chat/chatDirectPushMessage';
import {ChatPrecogContainer} from '../../activity/chat/chatPrecogContainer';
import {CommentPrecog} from '../../activity/comment/commentPrecog';
import {DuplicateConversationBannerContainer} from '../../activity/duplicateConversationBanner/duplicateConversationBannerContainer';
import {renderMessageDrafts} from '../../activity/message/messageDrafts';
import {MessagePrecog} from '../../activity/message/messagePrecog';
import {MessageViewerContainer} from '../../activity/message/messageViewerContainer';
import {GoOnlineBanner, GoOnlineBannerRenderLocationsEnum} from '../../activity/offline/goOnlineBanner';
import {LastSyncedActivity} from '../../activity/offline/lastSyncedActivity';
import {SendingActivityViewer} from '../../activity/sendingActivityViewer';
import {SplitConversationBannerContainer} from '../../activity/splitConversationBanner/splitConversationBannerContainer';
import {StaticViewerContainer} from '../../activity/static/staticViewerContainer';
import {MacroTriggersChatTimeline} from '../../macroTriggers/macroTriggersChatTimeline';
import {UnreadBannerAboveContainer} from '../../unreadBanners2/unreadBannerAboveContainer';
import {UnreadBannerBelowContainer} from '../../unreadBanners2/unreadBannerBelowContainer';
import {ConversationTimelineMarkAsReadOnLoad} from './conversationTimelineMarkAsReadOnLoad';
import {ConversationTimelineMilestone} from './conversationTimelineMilestone';
import {ConversationTimelineSeenMarkerContainer} from './conversationTimelineSeenMarkerContainer';
import {useOpenComposerByDefaultEffect} from './hooks/synchronousChannels';
import {useChatOptimisticMessages} from './hooks/useOptimisticMessages';

/*
 * Constants.
 */

const helpCenterDataLeakUrl = `${helpCenterUrl}/t/m2bx52/how-smart-merge-works#why-did-my-smart-merged-conversation-split-into-a-new-conversation`;

/*
 * Props.
 */

export interface ConversationTimelinePublicProps {
  panelId: ViewerPanelId;
  userId: number;
  userAlias: string;
  page: ViewerPanelConversationPageState;
  conversation: ConversationModel;
  inboxes: ResourceMap<InboxModel>;
  findTeammate: FindTeammate;

  activities: ReadonlyArray<ActivityModel>;
  chatDirectPushMessages: ReadonlyArray<InboundOptimisticMessage>;
  orphanSentMessages: ReadonlyArray<DraftMessageModel | TimelineMessageModel>;
  sendingActivities: ReadonlyArray<SendingActivity>;
  commentingTeammates: ReadonlyArray<TeammateModel>;
  hasChatPrecog: boolean;
  replyingTeammates: ReadonlyArray<TeammateModel>;
  draftSeedAuthors: ReadonlyArray<TeammateModel>;
  composeDraftMessages: ReadonlyArray<DraftMessageModel>;
  userComposeDraftMessages: ReadonlyArray<DraftMessageModel>;
  readonlyDraftMessageUids: ReadonlySet<string>;
  lastActivityCommentAuthorId?: number;
  canceledReminder?: ReminderModel;
  messageUidsWithNewCalendarEvents: ReadonlySet<string>;
  canComposerPopout: boolean;

  replyChannel?: ChannelModel;
  fetchMore: LoadMoreHandler;
  deleteEmptyDrafts: () => void;

  isSpam: boolean;
  fetchedAt: number | undefined;

  timelineVariation: TimelineVariationsEnum;

  isReviewingAiTags?: boolean;
  lastSeenMessageUid?: string;
  firstNewUnreadMessageUid?: string;
  hasSplitSingleThreadedConvos: boolean;
  lastMessage: MessageModel | undefined;
  lastMessageActivityId: number | undefined;
  hasMacroTriggers: boolean;

  staticViewerActivity: StaticViewerActivity | undefined;
}

/*
 * Types.
 */

type ComputeActivityRefs = (activityRefs: ActivityRefs) => ActivityRefs;

export type ConversationTimelineProps = ConversationTimelinePublicProps & ChatPushProps;

/*
 * Style.
 */

const StyledTimelineScrollBoundaries = styled(DynamicScrollBoundaries)`
  position: relative;
  grid-area: timeline;
`;

const StyledTimelineScroller = styled(VariableHeightScroller)`
  & > div {
    padding: 0 16px 16px 16px;
    padding-left: 6px;
    padding-right: 14px;

    display: flex;
    flex-direction: column;
  }
`;

const StyledLoaderDiv = styled.div`
  height: 40px;
  ${center()};
  margin: 8px 0 -8px 0;
`;

/*
 * Component.
 */

interface ConversationTimelineState {
  focusKey: Key | undefined;
  jumpUid: string | undefined;
  lastSendingCommentActivityUid?: string;
}

export class ConversationTimeline extends Component<ConversationTimelineProps, ConversationTimelineState> {
  private activityRefs: ActivityRefs;

  constructor(props: ConversationTimelineProps) {
    super(props);

    this.activityRefs = {};

    this.state = {
      focusKey: findFocusKey(props),
      jumpUid: props.page.jumpUid,
    };
  }

  /*
   * Lifecycle.
   */

  componentWillUnmount() {
    this.maybeDeleteEmptyDrafts();
  }

  /*
   * Delete unsent draft logic.
   */

  private maybeDeleteEmptyDrafts() {
    const alwaysShowReplyComposer = this.props.replyChannel?.behaviors.alwaysShowReplyComposer;
    const isSynchronous = this.props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS;

    if (alwaysShowReplyComposer || isSynchronous) {
      this.props.deleteEmptyDrafts();
    }
  }

  /*
   * Focus scroll logic.
   */

  static getDerivedStateFromProps(
    props: Readonly<ConversationTimelineProps>,
    state: Readonly<ConversationTimelineState>,
  ): Partial<ConversationTimelineState> | null {
    const {jumpUid} = props.page;
    const derivedState: Partial<ConversationTimelineState> = {
      jumpUid,
    };

    // If there's a new comment from our user, focus it (unless it's a highlight).
    const lastSendingCommentActivity = findLast(props.sendingActivities, (a) =>
      Boolean(a.comment && a.comment.authorId === props.userId && !commentOnlyHasAnnotation(a.comment)),
    );
    if (
      lastSendingCommentActivity &&
      lastSendingCommentActivity.uid !== state.lastSendingCommentActivityUid
    ) {
      return {
        ...derivedState,
        focusKey: lastSendingCommentActivity.uid,
        lastSendingCommentActivityUid: lastSendingCommentActivity.uid,
      };
    }

    // If the content page indicates a jump, focus on that activity.
    if (jumpUid !== state.jumpUid) {
      return {
        ...derivedState,
        focusKey: findFocusKey(props),
      };
    }

    return derivedState;
  }

  /*
   * Render.
   */

  render() {
    const {page, fetchMore, activities, orphanSentMessages, sendingActivities, composeDraftMessages} =
      this.props;
    const conversationId = this.props.conversation.id;

    const {moreStatusTop, moreStatusBottom, cellsContext} = page;
    const isAllLoadedBottom = moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED;
    const firstActivity = first(activities);
    const firstActivityId = firstActivity?.id || undefined;

    // Find the first message activities.
    const firstMessageActivityIds =
      page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED
        ? chain(activities)
            .filter((a) => a.visualType === ActivityVisualTypesEnum.MESSAGE)
            .map((a) => a.id)
            .value()
        : undefined;

    // Find the first comment activity.
    const firstCommentActivity =
      page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
      activities.find((a) => a.visualType === ActivityVisualTypesEnum.COMMENT);
    const firstCommentActivityId = (firstCommentActivity && firstCommentActivity.id) || undefined;

    // Find the last message activity.
    const hasSendingMessageActivity = sendingActivities.some((c) => Boolean(c.message));
    const hasDraftMessage = composeDraftMessages.length > 0;
    const lastTimelineMessage = findLastTimelineManualMessage(
      activities,
      orphanSentMessages,
      sendingActivities,
      hasDraftMessage,
    );

    // find the last inbound message activity
    const lastIncomeMessageActivity =
      page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
      findLast(
        activities,
        (a) =>
          a.visualType === ActivityVisualTypesEnum.MESSAGE && a.type === MessageActivityTypesEnum.INBOUND,
      );
    const lastIncomeMessageActivityId =
      (lastIncomeMessageActivity && lastIncomeMessageActivity.id) || undefined;

    // Infinite scroll logic.
    const infinite: InfiniteScrollerProps = {
      isLoadingMoreTop: moreStatusTop === AsyncMoreStatusesEnum.LOADING,
      hasMoreTop: moreStatusTop === AsyncMoreStatusesEnum.HAS_MORE,
      hasMoreBottom: moreStatusBottom === AsyncMoreStatusesEnum.HAS_MORE,
      loadMoreThreshold: 100,
      loadMore: (direction) => fetchMore(direction),
      loaderRenderer: (direction) => renderLoader(page, direction),
    };

    const {focusKey} = this.state;
    const focusMode =
      focusKey === findParametersActivityId(page.parameters) ? FocusModesEnum.MIDDLE : FocusModesEnum.TOP;
    return (
      <>
        <ConversationTimelineMarkAsReadOnLoad cellsContext={cellsContext} conversationId={conversationId} />
        <ConversationTimelineSeenMarkerContainer
          cellsContext={cellsContext}
          conversationId={conversationId}
        />
        <ConversationTimelineContent
          {...this.props}
          {...this.state}
          hasSendingMessageActivity={hasSendingMessageActivity}
          focusMode={focusMode}
          infinite={infinite}
          firstActivityId={firstActivityId}
          firstMessageActivityIds={firstMessageActivityIds}
          lastIncomeMessageActivityId={lastIncomeMessageActivityId}
          lastTimelineMessage={lastTimelineMessage}
          firstCommentActivityId={firstCommentActivityId}
          isAllLoadedBottom={isAllLoadedBottom}
          activityRefs={this.activityRefs}
          setActivityRefs={(computeActivityRefs: ComputeActivityRefs) => {
            this.activityRefs = computeActivityRefs(this.activityRefs);
          }}
        />
      </>
    );
  }
}

/*
 * Sub-Component.
 */

interface ConversationTimelineContentProps extends ConversationTimelineProps, ConversationTimelineState {
  hasSendingMessageActivity: boolean;
  focusMode: FocusModesEnum;
  infinite: InfiniteScrollerProps;
  firstActivityId?: number;
  firstMessageActivityIds: ReadonlyArray<number> | undefined;
  lastIncomeMessageActivityId?: number;
  lastTimelineMessage: LastTimelineMessage;
  firstCommentActivityId?: number;
  isAllLoadedBottom: boolean;
  replyChannel?: ChannelModel;
  deleteEmptyDrafts: () => void;
  timelineVariation: TimelineVariationsEnum;
  activityRefs: ActivityRefs;
  setActivityRefs: (computeActivityRefs: ComputeActivityRefs) => void;
}

const ConversationTimelineContent: FC<ConversationTimelineContentProps> = (props) => {
  const theme = useUserTheme();
  const originalLastActivityDate = findOriginalLastActivityDateForViewerPage(props.page);

  const {page, focusKey} = props;
  const mostRecentActivityId =
    page.moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED ? findLastActivityId(props) : undefined;
  const [isInitiallyFocusedOnLessRecentActivity] = useState(
    () =>
      focusKey === MILESTONE_ACTIVITY ||
      (isActivityIdFocusKey(focusKey) && focusKey !== mostRecentActivityId),
  );
  const [isAtOrBelowMostRecentActivity, setIsAtOrBelowMostRecentActivity] = useState<boolean>();
  const [firstVisibleActivityDate2, setFirstVisibleActivityDate2] = useState<number | undefined>(undefined);
  const [lastVisibleActivityDate2, setLastVisibleActivityDate2] = useState<number | undefined>(undefined);

  const ticketStatusTags = useAppSelector(getTicketStatusTags);

  const {isConnected} = usePush();

  const {
    isSpam,
    activities,
    focusMode,
    infinite,
    firstMessageActivityIds,
    lastIncomeMessageActivityId,
    lastTimelineMessage,
    isAllLoadedBottom,
    firstCommentActivityId,
    firstActivityId,
    composeDraftMessages,
    panelId,
    conversation,
    replyChannel,
    timelineVariation,
    userId,
    activityRefs,
    setActivityRefs,
    hasMacroTriggers,
    chatDirectPushMessages,
  } = props;

  const {optimisticMessages} = useChatOptimisticMessages(chatDirectPushMessages, conversation.id);

  const {originalConversationState} = page;
  const hasOriginalUnreadActivities = useMemo(() => {
    if (!originalConversationState) {
      return false;
    }

    const {seenAt, lastActivityDate} = originalConversationState;
    return activities.some(
      (a) => !seenAt || !lastActivityDate || (seenAt < a.date && lastActivityDate >= a.date),
    );
  }, [activities, originalConversationState]);

  // Separate read activities from unread ones.
  const [unreadActivities, readActivities] = useMemo(() => {
    if (!originalConversationState) {
      return [[], activities] as const;
    }

    const {seenAt} = originalConversationState;
    return partition(activities, (a) => !seenAt || seenAt < a.date);
  }, [activities, originalConversationState]);

  const [readActivityIds2, setReadActivityIds2] = useState(
    readActivities.map((activity) => `${activity.id}`),
  );

  const shouldOpenComposerByDefault = timelineVariation === TimelineVariationsEnum.SYNCHRONOUS;
  // We should show macro triggers for any conversation that open the composer by default and has the FF enabled.
  const shouldShowMacroTriggersInTimeline = shouldOpenComposerByDefault && hasMacroTriggers;

  // This hook creates an empty draft by default for a conversation, which opens the composer
  // It is only enabled for synchronous channels that have their specific channel FF enabled as well.
  useOpenComposerByDefaultEffect(
    panelId,
    composeDraftMessages,
    conversation,
    userId,
    isAllLoadedBottom,
    shouldOpenComposerByDefault,
    replyChannel,
  );

  const updateLastVisibleActivity2 = (visibleActivityIds: ReadonlyArray<string>) => {
    const firstVisibleActivityId = first(visibleActivityIds);
    const lastVisibleActivityId = last(visibleActivityIds);

    const firstVisibleActivity = activities.find(
      (a) => firstVisibleActivityId !== undefined && `${a.id}` === `${firstVisibleActivityId}`,
    );
    const lastVisibleActivity = activities.find(
      (a) => lastVisibleActivityId !== undefined && `${a.id}` === `${lastVisibleActivityId}`,
    );

    setFirstVisibleActivityDate2(firstVisibleActivity?.date);

    setLastVisibleActivityDate2(lastVisibleActivity?.date);

    setReadActivityIds2((readActivityIds) => [
      ...new Set([...readActivityIds, ...visibleActivityIds.map((activityId) => `${activityId}`)]),
    ]);
  };

  const updateLastVisibleActivityDebounced = useDebounce<VisibleItemsChangeHandler>(
    (computeVisibleItems, isAtOrBelowChild) => {
      if (!activities.length) {
        return;
      }

      const visibleActivityIds = computeVisibleItems(activityRefs).map((key) => `${key}`);

      updateLastVisibleActivity2(visibleActivityIds);
      setIsAtOrBelowMostRecentActivity(mostRecentActivityId ? isAtOrBelowChild(mostRecentActivityId) : false);
    },
    200,
  );

  return (
    <StyledTimelineScrollBoundaries hasBottomBoundary={false} color={theme.greys.shade30}>
      <StyledTimelineScroller
        keepScrollDirection={ScrollDirectionsEnum.BOTTOM}
        jumpKey={page.jumpUid}
        focusKey={props.focusKey}
        focusMode={focusMode}
        focusMargin={findFocusMargin(props, props.focusKey)}
        infinite={infinite}
        onVisibleItemsChange={updateLastVisibleActivityDebounced}
        interactionId="conversation-timeline"
        className={timelineScrollerClassName}
        role="feed"
        onScroll={(event) =>
          startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.VIEWER_SCROLL)
        }
      >
        {maybeRenderGoOnlineBanner(props, isConnected) || []}
        {maybeRenderSplitConversationBanner(props) || []}
        {/* Note that StyledTimelineScroller expects all these scrollable children to have matching keys and be rendered at the same, root level */}
        {renderActivities(
          {
            props,
            isSpam,
            originalLastActivityDate,
            hasOriginalUnreadActivities,
            unreadActivities,
            readActivities,
            firstMessageActivityIds,
            lastIncomeMessageActivityId,
            lastTimelineMessage,
            firstCommentActivityId,
            firstActivityId,
            focusKey: props.focusKey,
            ticketStatusTags,
          },
          setActivityRefs,
        )}
        {(isAllLoadedBottom &&
          renderAllLoadedBottom(
            props,
            isSpam,
            isConnected,
            lastTimelineMessage,
            shouldShowMacroTriggersInTimeline,
            optimisticMessages,
          )) ||
          []}
      </StyledTimelineScroller>
      <UnreadBannerAboveContainer
        originalConversationState={page.originalConversationState}
        panelId={props.panelId}
        userId={userId}
        activities={activities}
        readActivityIds={readActivityIds2}
        setReadActivityIds={(computeActivityIds) => setReadActivityIds2(computeActivityIds)}
        firstVisibleActivityDate={firstVisibleActivityDate2}
        originalLastActivityDate={originalLastActivityDate}
      />
      <UnreadBannerBelowContainer
        originalConversationState={page.originalConversationState}
        panelId={props.panelId}
        userId={userId}
        activities={activities}
        readActivityIds={readActivityIds2}
        setReadActivityIds={(computeActivityIds) => setReadActivityIds2(computeActivityIds)}
        lastVisibleActivityDate={lastVisibleActivityDate2}
        originalLastActivityDate={originalLastActivityDate}
        isInitiallyFocusedOnLessRecentActivity={isInitiallyFocusedOnLessRecentActivity}
        isAtOrBelowMostRecentActivity={isAtOrBelowMostRecentActivity}
      />
    </StyledTimelineScrollBoundaries>
  );
};

/*
 * Section renderers.
 */

interface RenderActivitiesOptions {
  props: ConversationTimelineContentProps;
  isSpam: boolean;
  originalLastActivityDate: number;
  hasOriginalUnreadActivities: boolean;
  unreadActivities: ReadonlyArray<ActivityModel>;
  readActivities: ReadonlyArray<ActivityModel>;
  firstActivityId?: number;
  firstMessageActivityIds: ReadonlyArray<number> | undefined;
  lastIncomeMessageActivityId?: number;
  lastTimelineMessage: LastTimelineMessage;
  firstCommentActivityId?: number;
  focusKey?: Key;
  ticketStatusTags: ResourceMap<CommonTagModel>;
}

function renderActivities(
  options: RenderActivitiesOptions,
  setActivityRefs: (computeActivityRefs: ComputeActivityRefs) => void,
) {
  const {
    props,
    isSpam,
    originalLastActivityDate,
    hasOriginalUnreadActivities,
    unreadActivities,
    readActivities,
    firstMessageActivityIds,
    lastIncomeMessageActivityId,
    firstCommentActivityId,
    lastTimelineMessage,
    firstActivityId,
    focusKey,
    ticketStatusTags,
  } = options;
  const {page, conversation, activities} = props;
  const isOnlyMessage = conversation.numMessages === 1;
  const isOnlyActivity = isOnlyMessage && !conversation.lastComment;
  const lastActivity = findLast(activities, isActivityInConversationRelevant);
  const hasActivitiesBelow =
    props.orphanSentMessages.length ||
    props.sendingActivities.length ||
    props.commentingTeammates.length ||
    props.replyingTeammates.length ||
    props.draftSeedAuthors.length;

  const canceledReminderActivityId = findCanceledReminderActivity(
    activities,
    props.canceledReminder,
    lastTimelineMessage.activityId,
  );

  const originalSeenAt = page.originalConversationState ? page.originalConversationState.seenAt : Infinity;

  // Build a method to render an array of activities.
  const renderActivityArray = (array: ReadonlyArray<ActivityModel>) => {
    const filteredActivities = array.filter((...args) =>
      filterStaticViewerBannerActivity(page.cellsContext, props.staticViewerActivity, ...args),
    );

    const activityGroups = filteredActivities
      .reduce<Array<Array<ActivityModel>>>(
        (currentActivityGroups, activity) => {
          if (
            activity.visualType === ActivityVisualTypesEnum.BASIC &&
            isBasicActivityCollapsible(
              props.userId,
              props.findTeammate,
              props.inboxes,
              activity,
              ticketStatusTags,
            )
          ) {
            // Sequential low-priority activities are grouped together.
            currentActivityGroups[currentActivityGroups.length - 1].push(activity);
          }
          // Non-basic activities always belong to their own group.
          else {
            currentActivityGroups.push([activity], []);
          }

          return currentActivityGroups;
        },
        [[]],
      )
      // There may be empty arrays from sequential non-basic activities, which we filter out here.
      .filter((activityGroup) => activityGroup.length);

    // <ActivityViewer /> requires the overall index for each activity, even when separated into groups.
    return activityGroups.map((activityGroup, groupIndex) => {
      // True start index of this activity group can be computed from size of all previous groups.
      const activityIndexStart = activityGroups.slice(0, groupIndex).reduce((a, b) => a + b.length, 0);

      const activityViewers = activityGroup.map((activity, activityIndex) => {
        const index = activityIndexStart + activityIndex;

        // Show only the _first_ new message to be "new" to avoid showing multiple "new" pills in quick succession in the UI.
        const isNewUnread =
          options.props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
            ? isMessageActivity(activity) && options.props.firstNewUnreadMessageUid === activity.messageUid
            : originalLastActivityDate < activity.date;

        return (
          <ActivityViewer
            key={activity.id}
            ref={(ref) =>
              setActivityRefs((activityRefs: ActivityRefs) => ({...activityRefs, [activity.id]: ref}))
            }
            focusKey={focusKey}
            panelId={props.panelId}
            cellsContext={page.cellsContext}
            conversationId={conversation.id}
            conversationActionContext={makeConversationActionContext(page.cellsContext, conversation)}
            conversationMessageType={conversation.messageType}
            isSpam={isSpam}
            namespace={conversation.namespace}
            replyChannel={props.replyChannel}
            isLastMessage={lastTimelineMessage.activityId === activity.id}
            activities={filteredActivities}
            activityStates={page.activities}
            activityIndex={index}
            isUnread={!originalSeenAt || originalSeenAt < activity.date}
            isNewUnread={isNewUnread}
            firstMessageActivityIds={firstMessageActivityIds}
            isFirstMessage={activity.id === first(firstMessageActivityIds) && activity.id === firstActivityId}
            isFirstComment={activity.id === firstCommentActivityId && activity.id === firstActivityId}
            isLastActivity={lastActivity && activity.id === lastActivity.id && !hasActivitiesBelow}
            isFirstActivity={activity.id === firstActivityId}
            isOnlyMessage={isOnlyMessage}
            lastIncomeMessageActivityId={lastIncomeMessageActivityId}
            canceledReminder={
              (activity.id === canceledReminderActivityId && props.canceledReminder) || undefined
            }
            isOnlyActivity={isOnlyActivity}
            userId={props.userId}
            jumpKey={page.jumpUid}
            jumpSourceActivityId={page.jumpSourceActivityId}
            isHighlighted={activity.id === findParametersActivityId(page.parameters)}
            containsNewCalendarEvent={containsNewCalendarEvent(props, activity)}
            timelineVariation={props.timelineVariation}
            shouldComputeScrollerVisibility
            isReviewingAiTags={props.isReviewingAiTags}
            lastSeenMessageUid={props.lastSeenMessageUid}
          />
        );
      });

      // If this is not a basic activity group, do not wrap in a group div.
      if (activityGroup.length === 1 && activityGroup[0].visualType !== ActivityVisualTypesEnum.BASIC) {
        return activityViewers;
      }

      // For a group of basic activities, wrap in a div to align by widest content and left-align.
      // The grid attributes for a basic activity row are set in <BasicViewerBase />
      const key = `activity_group_${activityGroup[activityGroup.length - 1].id}`;
      return <BasicViewerGroup key={key}>{activityViewers}</BasicViewerGroup>;
    });
  };
  // Check if we have any activities that were unread before opening the conversation.
  if (!hasOriginalUnreadActivities) {
    return renderActivityArray(activities);
  }

  // If there are no unread activities, just render the read ones.
  if (unreadActivities.length === 0) {
    return renderActivityArray(readActivities);
  }

  // If there are no read activities, just render the unread ones.
  if (readActivities.length === 0) {
    return renderActivityArray(unreadActivities);
  }

  // Otherwise, render both with a milestone in the middle.
  return [
    ...renderActivityArray(readActivities),
    <ConversationTimelineMilestone key={MILESTONE_ACTIVITY}>
      <Trans id="timeline-newactivity">New activity</Trans>
    </ConversationTimelineMilestone>,
    ...renderActivityArray(unreadActivities),
  ];
}

function renderAllLoadedBottom(
  props: ConversationTimelineContentProps,
  isSpam: boolean,
  isConnected: boolean,
  lastTimelineMessage: LastTimelineMessage,
  shouldShowMacroTriggersInTimeline: boolean,
  optimisticMessages: ReadonlyArray<InboundOptimisticMessage>,
) {
  const {conversation, orphanSentMessages, composeDraftMessages, timelineVariation} = props;

  const {cellsContext} = props.page;
  const isInTeamContext = isConversationInTeamContext(cellsContext, conversation);

  // Find the last activity.
  const lastActivity = last(props.activities);
  const lastActivityMessageUid =
    lastActivity?.visualType === ActivityVisualTypesEnum.MESSAGE ? lastActivity.messageUid : undefined;

  return concat(
    // Messages that have been sent, but don't have an activity or loaded/timeline message yet.
    orphanSentMessages.map((message, index) => (
      <MessageViewerContainer
        key={message.uid}
        panelId={props.panelId}
        cellsContext={cellsContext}
        namespace={conversation.namespace}
        conversationId={conversation.id}
        conversationActionContext={makeConversationActionContext(cellsContext, conversation)}
        isSpam={isSpam}
        replyChannel={
          lastTimelineMessage.orphanSentMessageUid === message.uid ? props.replyChannel : undefined
        }
        timelineVariation={timelineVariation}
        messageUid={message.uid}
        previousMessageUid={index === 0 ? lastActivityMessageUid : orphanSentMessages[index - 1].uid}
      />
    )),
    // Messages and comments that are sending.
    renderSendingActivities(
      props.panelId,
      conversation,
      isSpam,
      props.sendingActivities,
      cellsContext,
      timelineVariation,
      lastTimelineMessage,
      lastActivity,
      last(orphanSentMessages)?.uid,
      props.replyChannel,
    ),
    // Messages received by direct push notification that aren't reconciled yet
    maybeRenderDirectPushMessage(
      optimisticMessages,
      props.panelId,
      conversation,
      cellsContext,
      props.sendingActivities,
      orphanSentMessages,
      props.activities,
    ),
    maybeRenderCommentPrecog(
      props.commentingTeammates,
      props.lastActivityCommentAuthorId,
      props.hasSendingMessageActivity,
      props.timelineVariation,
      props.panelId,
    ),
    maybeRenderChatPrecog(props),
    renderStaticActivities(isInTeamContext, conversation, props.staticViewerActivity),
    maybeRenderDuplicateConversationBanner(props),
    maybeRenderLastSynced(props, isConnected),
    maybeRenderDataRetentionBanner(props.conversation),
    maybeRenderMessagePrecog(props.replyingTeammates, composeDraftMessages, props.timelineVariation),
    maybeRenderDraftSeed(
      props.replyingTeammates,
      composeDraftMessages,
      props.draftSeedAuthors,
      props.timelineVariation,
    ),
    maybeRenderMacroTriggers(
      props.panelId,
      shouldShowMacroTriggersInTimeline,
      props.lastMessage,
      props.lastMessageActivityId,
    ),
    ...renderTimelineDrafts(props),
  );
}

function renderSendingActivities(
  panelId: ViewerPanelId,
  conversation: ConversationModel,
  isSpam: boolean,
  sendingActivities: ReadonlyArray<SendingActivity>,
  cellsContext: CellsContext,
  timelineVariation: TimelineVariationsEnum,
  lastTimelineMessage: LastTimelineMessage,
  lastActivity: ActivityModel | undefined,
  lastOrphanSentMessageUid: string | undefined,
  replyChannel: ChannelModel | undefined,
) {
  return sendingActivities.map((s, index, array) => (
    <SendingActivityViewer
      key={s.uid}
      panelId={panelId}
      cellsContext={cellsContext}
      namespace={conversation.namespace}
      conversationId={conversation.id}
      conversationActionContext={makeConversationActionContext(cellsContext, conversation)}
      isSpam={isSpam}
      replyChannel={lastTimelineMessage.sendingActivityIndex === index ? replyChannel : undefined}
      sendingActivity={s}
      previousSendingActivity={array[index - 1]}
      nextSendingActivity={array[index + 1]}
      isLastActivity={index === array.length - 1}
      lastActivity={lastActivity}
      lastOrphanSentMessageUid={lastOrphanSentMessageUid}
      timelineVariation={timelineVariation}
    />
  ));
}

function renderStaticActivities(
  isInTeamContext: boolean,
  conversation: ConversationModel,
  staticViewerActivity: StaticViewerActivity | undefined,
) {
  // Only render static activities for team conversations in the teammate context.
  if (isInTeamContext || !staticViewerActivity) {
    return [];
  }

  return (
    <StaticViewerContainer
      key="timeline-static"
      conversation={conversation}
      staticViewerActivity={staticViewerActivity}
    />
  );
}

function renderLoader(page: ViewerPanelConversationPageState, direction: ScrollDirectionsEnum) {
  const isTop = direction === ScrollDirectionsEnum.TOP;
  const moreStatus = isTop ? page.moreStatusTop : page.moreStatusBottom;

  if (moreStatus === AsyncMoreStatusesEnum.HAS_MORE) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.MEDIUM} variant={LoaderVariantsEnum.GREY} isEnabled={false} />
      </StyledLoaderDiv>
    );
  }

  if (moreStatus === AsyncMoreStatusesEnum.LOADING) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.MEDIUM} variant={LoaderVariantsEnum.GREY} />
      </StyledLoaderDiv>
    );
  }

  return null;
}

function maybeRenderDirectPushMessage(
  chatDirectPushMessages: ReadonlyArray<InboundOptimisticMessage>,
  panelId: ViewerPanelId,
  conversation: ConversationModel,
  cellsContext: CellsContext,
  sendingActivities: ReadonlyArray<SendingActivity>,
  orphanSentMessages: ReadonlyArray<DraftMessageModel | TimelineMessageModel>,
  activities: ReadonlyArray<ActivityModel>,
) {
  if (chatDirectPushMessages.length === 0) {
    return [];
  }

  return chatDirectPushMessages.map((message, index) => (
    <ChatDirectPushMessage
      key={`chat_push_${message.id}`}
      message={message}
      panelId={panelId}
      conversationNamespace={conversation.namespace}
      conversationId={conversation.id}
      cellsContext={cellsContext}
      shouldShowAvatar={computeShouldRenderAvatar(index, sendingActivities, orphanSentMessages, activities)}
    />
  ));
}

function computeShouldRenderAvatar(
  messagePos: number,
  sendingActivities: ReadonlyArray<SendingActivity>,
  orphanSentMessages: ReadonlyArray<DraftMessageModel | TimelineMessageModel>,
  activities: ReadonlyArray<ActivityModel>,
) {
  // Render avatar only: there is a sending activity or orphan sent message or last activity is not inbound (not from chat).
  return (
    messagePos === 0 &&
    (sendingActivities.length > 0 ||
      orphanSentMessages.length > 0 ||
      last(activities)?.type !== MessageActivityTypesEnum.INBOUND)
  );
}

function maybeRenderCommentPrecog(
  commentingTeammates: ReadonlyArray<TeammateModel>,
  lastActivityCommentAuthorId: number | undefined,
  hasSendingMessageActivity: boolean,
  timelineVariation: TimelineVariationsEnum,
  panelId: ViewerPanelId,
) {
  if (commentingTeammates.length === 0) {
    return [];
  }

  const shouldHideTitle =
    !hasSendingMessageActivity && onlyItem(commentingTeammates)?.id === lastActivityCommentAuthorId;

  return (
    <CommentPrecog
      key="comment-precog"
      teammates={commentingTeammates}
      shouldHideTitle={shouldHideTitle}
      timelineVariation={timelineVariation}
      panelId={panelId}
    />
  );
}

function maybeRenderChatPrecog(props: ConversationTimelineContentProps) {
  const {hasChatPrecog, conversation, page, timelineVariation} = props;
  if (!hasChatPrecog) {
    return [];
  }

  return (
    <ChatPrecogContainer
      key="chat-precog"
      conversation={conversation}
      teammateId={page.cellsContext.teammateId}
      timelineVariation={timelineVariation}
    />
  );
}

function maybeRenderMessagePrecog(
  replyingTeammates: ReadonlyArray<TeammateModel>,
  draftComposeMessages: ReadonlyArray<DraftMessageModel>,
  timelineVariation: TimelineVariationsEnum,
) {
  const draftAuthorIds = draftComposeMessages.map((d) => d.authorId);
  const replyingTeammatesWithoutDraft = replyingTeammates.filter((t) => !draftAuthorIds.includes(t.id));
  if (replyingTeammatesWithoutDraft.length === 0) {
    return [];
  }

  return (
    <MessagePrecog
      key="message-precog"
      teammates={replyingTeammatesWithoutDraft}
      timelineVariation={timelineVariation}
      areTeammatesReplying
    />
  );
}

function maybeRenderDraftSeed(
  replyingTeammates: ReadonlyArray<TeammateModel>,
  draftComposeMessages: ReadonlyArray<DraftMessageModel>,
  draftSeedAuthors: ReadonlyArray<TeammateModel>,
  timelineVariation: TimelineVariationsEnum,
) {
  const draftAuthorIds = draftComposeMessages.map((d) => d.authorId);
  const replyingTeammateIds = replyingTeammates.map((t) => t.id);

  const draftSeedAuthorsWithoutPrecogOrDraft = draftSeedAuthors.filter(
    (t) => !draftAuthorIds.includes(t.id) && !replyingTeammateIds.includes(t.id),
  );

  if (draftSeedAuthorsWithoutPrecogOrDraft.length === 0) {
    return [];
  }

  return (
    <MessagePrecog
      key="draft-seed-precog"
      teammates={draftSeedAuthorsWithoutPrecogOrDraft}
      timelineVariation={timelineVariation}
    />
  );
}

function maybeRenderGoOnlineBanner(props: ConversationTimelineContentProps, isConnected: boolean) {
  if (isConnected) {
    return null;
  }

  if (props.page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED) {
    return null;
  }

  return <GoOnlineBanner key="go-online" renderLocation={GoOnlineBannerRenderLocationsEnum.P3} />;
}

/** Although split conversation exists as an activity, we render it as a static banner. */
function maybeRenderSplitConversationBanner(props: ConversationTimelineContentProps) {
  // Find the first split conversation activity.
  const activity = findSplitTargetConversationActivity(props.activities);
  if (!activity) {
    return null;
  }

  const isSplitForPrivacy =
    activity.source.type === ActivitySourceTypesEnum.DATA_LEAK &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER;

  return (
    <SplitConversationBannerContainer
      key="split_conversation_banner"
      activity={activity}
      isSplitForPrivacy={isSplitForPrivacy}
      isEmailConversation={props.conversation.messageType === MessageTypesEnum.EMAIL}
      onDataLeakLearnMoreClick={onDataLeakLearnMoreClick}
      conversationChannelIds={props.conversation.channelIds}
    />
  );
}

function maybeRenderDuplicateConversationBanner(props: ConversationTimelineContentProps) {
  const {id: conversationId, duplicateConversationIds, shouldShowDuplicateBanner} = props.conversation;

  if (duplicateConversationIds.length === 0) {
    return [];
  }

  return (
    <DuplicateConversationBannerContainer
      key="duplicate-conversation-banner"
      duplicateConversationIds={duplicateConversationIds}
      conversationId={conversationId}
      shouldBannerHaveBeenHidden={!shouldShowDuplicateBanner}
    />
  );
}

function maybeRenderLastSynced(props: ConversationTimelineContentProps, isConnected: boolean) {
  if (isConnected) {
    return [];
  }

  return <LastSyncedActivity key="last-synced" lastSyncedDate={props.fetchedAt} />;
}

function maybeRenderDataRetentionBanner(conversation: ConversationModel) {
  if (conversation.teamStatus !== ConversationTeamStatusesEnum.TRASHED) {
    return [];
  }

  return (
    <BasicViewDataRetentionBannerContainer
      key="conversation-data-retention-banner"
      conversation={conversation}
    />
  );
}

function maybeRenderMacroTriggers(
  panelId: ViewerPanelId,
  shouldShowMacroTriggersInTimeline: boolean,
  lastMessage: MessageModel | undefined,
  lastMessageActivityId: number | undefined,
) {
  if (!shouldShowMacroTriggersInTimeline || isUndefined(lastMessage) || isUndefined(lastMessageActivityId)) {
    return [];
  }

  return <MacroTriggersChatTimeline key="macro-trigger" panelId={panelId} message={lastMessage} />;
}

function renderTimelineDrafts(props: ConversationTimelineContentProps) {
  // It shouldn't be disabled if there's no split; only split conversations have the "cannotReply" flag set to true
  const isDisabled = props.hasSplitSingleThreadedConvos && props.conversation.cannotReply;
  const splitActivity = findSplitOriginConversationActivity(props.activities);
  let maybeDisabledComposer: null | ReactNode = null;

  if (isDisabled && splitActivity) {
    maybeDisabledComposer = (
      <DisabledChatComposerPlaceholder
        key="DisabledChatComposerPlaceholder"
        timelineVariation={props.timelineVariation}
        targetConversationId={splitActivity.targetConversationId}
      />
    );
  }
  return compact([renderMessageDrafts(props), maybeDisabledComposer]);
}

/*
 * Helpers.
 */

function findCanceledReminderActivity(
  activities: ReadonlyArray<ActivityModel>,
  canceledReminder?: ReminderModel,
  lastMessageActivityId?: number,
) {
  if (!canceledReminder) {
    return undefined;
  }

  const canceledReminderActivity = activities.find(
    (activity) =>
      activity.visualType === ActivityVisualTypesEnum.MESSAGE &&
      activity.messageId === canceledReminder.canceledById,
  );

  if (canceledReminder.isCanceledByActivity) {
    return _.last(activities)?.id;
  }

  return (canceledReminderActivity && canceledReminderActivity.id) || lastMessageActivityId;
}

function findFocusMargin(props: ConversationTimelineContentProps, focusKey?: Key) {
  const defaultFocusMargin = 16;

  // If we're not focusing a "new activity" line, nothing to do.
  if (focusKey !== MILESTONE_ACTIVITY) {
    return defaultFocusMargin;
  }

  const {page, activities, focusMode} = props;

  // If we're not focusing from the top, nothing to do.
  if (focusMode !== FocusModesEnum.TOP) {
    return defaultFocusMargin;
  }

  // If we don't have an originalSeenAt, nothing to do.
  const originalSeenAt = page.originalConversationState?.seenAt;
  if (!originalSeenAt) {
    return defaultFocusMargin;
  }

  const firstUnreadActivity = activities.find(
    (a) => isActivityInConversationRelevant(a) && a.date > originalSeenAt,
  );

  if (!firstUnreadActivity) {
    return defaultFocusMargin;
  }

  // If our first unread activity isn't a message, nothing to do.
  if (firstUnreadActivity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
    return defaultFocusMargin;
  }

  // Otherwise, return a bigger margin to show the (collapsed) message in the viewport.
  return 72;
}

/** Find the first split target conversation activity available. */
function findSplitTargetConversationActivity(
  activities: ReadonlyArray<ActivityModel>,
): BasicActivitySplitTargetModel | undefined {
  return activities.find(
    (a) =>
      a.type === BasicActivityTypesEnum.SPLIT_TARGET &&
      a.visualType === ActivityVisualTypesEnum.BASIC &&
      a.basicVisualType === BasicVisualTypesEnum.SPLIT_TARGET &&
      a,
  ) as BasicActivitySplitTargetModel | undefined;
}

/** Find the first split origin conversation activity available. */
function findSplitOriginConversationActivity(
  activities: ReadonlyArray<ActivityModel>,
): BasicActivitySplitOriginModel | undefined {
  return activities.find(
    (a) =>
      a.type === BasicActivityTypesEnum.SPLIT_ORIGIN &&
      a.visualType === ActivityVisualTypesEnum.BASIC &&
      a.basicVisualType === BasicVisualTypesEnum.SPLIT_ORIGIN &&
      a,
  ) as BasicActivitySplitOriginModel | undefined;
}

function onDataLeakLearnMoreClick() {
  openInBrowser(helpCenterDataLeakUrl);
}

/* Returns whether the given activity is a message that contains a new calendar event. */
function containsNewCalendarEvent(props: ConversationTimelineProps, activity: ActivityModel) {
  if (activity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
    return false;
  }

  return props.messageUidsWithNewCalendarEvents.has(activity.messageUid);
}
